import * as React from 'react'
import { FaIcon, faXmark, faPrint } from '@/lib/fa'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { ReportType } from '@/app/constants/reportType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePersistentState } from '@/modules/shared/hooks/usePersistentState'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'

interface PrintOptions {
  generateToSeparateFiles: boolean
  rememberSelected: boolean
}

interface BusinessTripsPrintModalProps {
  isOpen: boolean
  pending?: boolean
  onClose: () => void
  companyId: string
  driverIds: string[]
  startTime: Date | string
  endTime: Date | string
  onGenerate: (selectedReports: ReportType[], options: PrintOptions) => void
}

type ReportOption = {
  type: ReportType
  labelKey: string
}

type PersistedPrintSelection = {
  reports: ReportType[]
  generateToSeparateFiles: boolean
}

const REPORT_OPTIONS: ReportOption[] = [
  {
    type: ReportType.TRIP_DETAILS,
    labelKey: 'menu.delegation-print-settlement',
  },
  {
    type: ReportType.BASIC_TRIP_PRINT,
    labelKey: 'menu.delegation-report',
  },
  {
    type: ReportType.GROUPED_TRIP_BASICS,
    labelKey: 'menu.grouped-delegation-report',
  },
  {
    type: ReportType.TRIP_GENERAL_STATEMENT,
    labelKey: 'menu.delegation-print-general-statement',
  },
  {
    type: ReportType.TRIP_INVOICES,
    labelKey: 'menu.delegation-print-invoices',
  },
  {
    type: ReportType.BUSINESS_TRIP_COMMAND,
    labelKey: 'menu.delegation-print-command',
  },
  {
    type: ReportType.SPECIFIC_BUSINESS_TRIP_COMMAND,
    labelKey: 'menu.delegation-print-specific',
  },
  {
    type: ReportType.TRIP_FOREIGN_COUNTRY,
    labelKey: 'menu.delegation-print-foreign-diet',
  },
  {
    type: ReportType.FOREIGN_ALLOWANCES,
    labelKey: 'menu.delegation-print-detailed-foreign-diet',
  },
  {
    type: ReportType.TRIP_SUMMARY,
    labelKey: 'printout.tripSummary',
  },
]

const ALLOWED_REPORTS = new Set(REPORT_OPTIONS.map((option) => option.type))

const DEFAULT_SELECTION: PersistedPrintSelection = {
  reports: [ReportType.TRIP_DETAILS],
  generateToSeparateFiles: false,
}

function sanitizeSelection(
  value: Partial<PersistedPrintSelection> | null | undefined,
): PersistedPrintSelection {
  const reports = Array.isArray(value?.reports)
    ? value.reports.filter((report) => ALLOWED_REPORTS.has(report))
    : []

  return {
    reports: reports.length > 0 ? reports : DEFAULT_SELECTION.reports,
    generateToSeparateFiles: Boolean(value?.generateToSeparateFiles),
  }
}

const BusinessTripsPrintModal: React.FC<BusinessTripsPrintModalProps> = (
  props,
) => {
  if (!props.isOpen) return null

  return (
    <BusinessTripsPrintModalContent key={props.companyId} {...props} />
  )
}

const BusinessTripsPrintModalContent: React.FC<BusinessTripsPrintModalProps> = ({
  onClose,
  companyId,
  onGenerate,
  pending,
}) => {
  const { t } = useLocalize()
  const [selection, setSelection] =
    usePersistentState<PersistedPrintSelection>(
      `business-trips-print-selection:${companyId}`,
      DEFAULT_SELECTION,
    )

  React.useEffect(() => {
    setSelection((prev) => {
      const sanitized = sanitizeSelection(prev)
      const sameReports =
        Array.isArray(prev.reports) &&
        sanitized.reports.length === prev.reports.length &&
        sanitized.reports.every((report, index) => report === prev.reports[index])
      if (
        sameReports &&
        sanitized.generateToSeparateFiles === Boolean(prev.generateToSeparateFiles)
      ) {
        return prev
      }
      return sanitized
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedReports = React.useMemo(() => {
    if (!Array.isArray(selection.reports)) {
      return new Set(DEFAULT_SELECTION.reports)
    }
    return new Set(
      selection.reports.filter((report) => ALLOWED_REPORTS.has(report)),
    )
  }, [selection.reports])

  const handleReportToggle = (reportType: ReportType) => {
    setSelection((prev) => {
      const updated = new Set(
        Array.isArray(prev.reports) ? prev.reports : [],
      )
      if (updated.has(reportType)) {
        updated.delete(reportType)
      } else {
        updated.add(reportType)
      }
      return { ...prev, reports: Array.from(updated) }
    })
  }

  const handleSelectAll = () => {
    if (selectedReports.size === REPORT_OPTIONS.length) {
      setSelection((prev) => ({ ...prev, reports: [] }))
    } else {
      setSelection((prev) => ({
        ...prev,
        reports: REPORT_OPTIONS.map((option) => option.type),
      }))
    }
  }

  const handleGenerate = () => {
    if (selectedReports.size === 0 || pending) {
      return
    }
    onGenerate(Array.from(selectedReports), {
      generateToSeparateFiles: selection.generateToSeparateFiles,
      rememberSelected: false,
    })
  }

  return (
    <ModalLayout customZIndex={9999}>
      <div className="tw-flex tw-w-[615px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white">
        {/* Header */}
        <div className="tw-flex tw-h-[64px] tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-px-6">
          <h2 className="tw-text-xl tw-font-semibold tw-leading-[28px] tw-text-[#1f2937]">
            {t('printout.generate-reports') || 'Generuj raporty'}
          </h2>
          <button
            onClick={onClose}
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100"
            aria-label="Close"
          >
            <FaIcon
              icon={faXmark}
              className="tw-h-5 tw-w-5 tw-text-[#1f2937]"
            />
          </button>
        </div>

        {/* Content */}
        <div className="tw-overflow-y-auto">
          {/* Table Header */}
          <div className="tw-sticky tw-top-0 tw-z-10 tw-grid tw-grid-cols-[40px_1fr] tw-border-b tw-border-[#e5e6e6] tw-bg-[#f2f2f2] tw-bg-opacity-25">
            <div className="tw-flex tw-h-8 tw-items-center tw-justify-center tw-p-2">
              <Checkbox
                checked={
                  selectedReports.size === REPORT_OPTIONS.length &&
                  REPORT_OPTIONS.length > 0
                }
                onChange={handleSelectAll}
                className="tw-h-4 tw-w-4"
              />
            </div>
            <div className="tw-flex tw-h-8 tw-items-center tw-gap-2 tw-px-3 tw-py-2">
              <span className="tw-text-xs tw-font-medium tw-leading-[14px] tw-text-[#1f2937]">
                {t('printout.report-name') || 'NAZWA WYDRUKU'}
              </span>
            </div>
          </div>

          {/* Report Options List */}
          <div className="tw-divide-y tw-divide-[#e5e6e6]">
            {REPORT_OPTIONS.map((option) => {
              const isSelected = selectedReports.has(option.type)
              const handleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
                // Don't trigger if clicking on the checkbox itself
                const target = e.target as HTMLElement
                if (
                  target.tagName === 'INPUT' ||
                  target.closest('input') ||
                  target.closest('label')
                ) {
                  return
                }
                handleReportToggle(option.type)
              }
              return (
                <div
                  key={option.type}
                  onClick={handleRowClick}
                  className="tw-grid tw-h-10 tw-cursor-pointer tw-grid-cols-[40px_1fr] tw-border-b tw-border-[#e5e6e6] hover:tw-bg-gray-50"
                >
                  <div className="tw-flex tw-items-center tw-justify-center tw-p-2">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleReportToggle(option.type)
                      }}
                      className="tw-h-4 tw-w-4"
                    />
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-border-r tw-border-[#e5e6e6] tw-px-3 tw-py-2">
                    <FaIcon
                      icon={faPrint}
                      className="tw-h-4 tw-w-4 tw-text-[#1f2937]"
                    />
                    <span className="tw-text-xs tw-font-medium tw-leading-[12px] tw-text-[#1f2937]">
                      {t(option.labelKey) || option.labelKey}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="tw-h-px tw-bg-[#e5e6e6]"></div>

        {/* Footer */}
        <div className="tw-flex tw-h-[70px] tw-items-center tw-border-t tw-border-[#e5e6e6] tw-bg-white tw-px-6">
          <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-6">
              {/* Generate to separate files checkbox */}
              <div className="tw-flex tw-h-4 tw-items-center tw-gap-2">
                <Checkbox
                  checked={Boolean(selection.generateToSeparateFiles)}
                  onChange={(e) =>
                    setSelection((prev) => ({
                      ...prev,
                      generateToSeparateFiles: (e.target as HTMLInputElement)
                        .checked,
                    }))
                  }
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-text-xs tw-font-normal tw-leading-[12px] tw-text-[#1f2937]">
                  {t('printout.generate-to-separate-files')}
                </span>
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-3">
              {pending && (
                <span className="tw-text-xs tw-text-content-base/70">
                  {t('printout.processing-in-progress')}
                </span>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleGenerate}
                loading={pending}
                disabled={selectedReports.size === 0}
                className="tw-h-[38px] tw-w-[130px] tw-rounded-lg tw-text-sm tw-font-medium tw-leading-[14px]"
              >
                {t('printout.generate-reports') || 'Generuj raporty'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default BusinessTripsPrintModal
